/* eslint-disable react/jsx-closing-tag-location */
// This linting disables are needed to make accesible html

/* eslint-disable jsx-a11y/no-noninteractive-tabindex */

/* eslint-disable jsx-a11y/no-redundant-roles */
import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { getEmptyImage } from 'react-dnd-html5-backend';

import { isRankingOrderAccessibilityEnabled } from '@services/featureFlags';
import { replaceVariables } from '@services/translations';

import stripHtml from '@utils/stripHtml';

import styles from './ReorderableItem.scss';
import { translations } from './constants';
import { withDndReorder } from './dnd-service';

const { shape, bool, func, number, string, instanceOf } = PropTypes;

export class BareReorderableItem extends React.PureComponent {
  static propTypes = {
    /** Draggable item visualization */
    children: shape(),
    /** Whether this item is being dragged at the time. Passed in automatically by DragSource */
    isDragging: bool,
    /** Function to connect this item as a drag source. Passed in automatically by DragSource */
    connectDragSource: func,
    /** Function to set the drag preview for this item. Passed in automatically by DragSource */
    connectDragPreview: func,
    /** Function to connect this item as a drop target. Passed in automatically by DragSource */
    connectDropTarget: func,
    /** Number defining actual index of the draggable item */
    rowIndex: number,
    /** Number defining the position of focused element */
    focusedIndex: number,
    /** Number defining the position of selected element */
    selectedIndex: number,
    /** Function to modify focusedIndex */
    setFocusedIndex: func,
    /** Value defining the if of element describing actual item */
    ariaDescribedBy: string,
    /** List ref */
    listRef: shape({ current: instanceOf(Element) }),
    caption: string,
    text: string
  };

  static defaultProps = {
    isDragging: false,
    connectDragSource: null,
    connectDragPreview: null,
    connectDropTarget: null,
    focusedIndex: undefined,
    selectedIndex: undefined,
    setFocusedIndex: () => {},
    ariaDescribedBy: '',
    children: null,
    rowIndex: null,
    caption: null
  };

  constructor(props) {
    super(props);
    this.state = {
      isEntering: false
    };
  }

  componentDidUpdate() {
    this.props.connectDragPreview(getEmptyImage());
    const currentRef = this.optionRef;
    if (!currentRef) {
      return;
    }

    this.handleFocus = (event) => {
      if (event.target === currentRef) {
        this.props.setFocusedIndex(this.props.rowIndex);
      }
    };

    currentRef.addEventListener('focus', this.handleFocus, true);
  }

  componentWillUnmount() {
    this.optionRef.removeEventListener('focus', this.handleFocus);
  }

  setEntering = () => {
    this.setState({ isEntering: true });
  };

  setEntered = () => {
    this.setState({ isEntering: false });
  };

  handleFocus = null;

  render() {
    const {
      children,
      isDragging,
      connectDragSource,
      connectDropTarget,
      ariaDescribedBy,
      focusedIndex,
      selectedIndex,
      rowIndex,
      listRef,
      actualItems,
      text
    } = this.props;

    const isSelected =
      selectedIndex !== undefined
        ? !Number.isNaN(selectedIndex) && focusedIndex === rowIndex
        : false;

    const itemStyles = classnames(styles.draggable_item, {
      [styles.dragging]: isDragging || this.state.isEntering,
      [styles.selected]: isSelected
    });

    // See: https://jira.medallia.com/browse/OA-26482 and https://jira.medallia.com/browse/OA-26473
    // Added aria-roledescription so as to announce the role "Draggable" and the current focused index out of total items as part of above tickets respectively.

    const roleDescription = replaceVariables(translations().DND_REORDERING_FOCUSED, {
      ITEM: stripHtml(actualItems[focusedIndex]?.text),
      POSITION: focusedIndex + 1,
      TOTAL: actualItems.length
    });

    const ariaDescription = isRankingOrderAccessibilityEnabled()
      ? {
          'aria-roledescription':
            selectedIndex === undefined && focusedIndex !== undefined ? `${roleDescription}` : ''
        }
      : { 'aria-describedBy': ariaDescribedBy };

    return listRef
      ? connectDropTarget(
          <li
            ref={(e) => {
              this.optionRef = e;
            }}
            listRef={listRef}
            tabIndex={0}
            role="listitem"
            {...ariaDescription}
            className={classnames({
              [styles.listItemDragging]: isDragging
            })}
          >
            <div className={itemStyles}>
              {connectDragSource(<div className={styles.draggable_item_content}>{children}</div>)}
            </div>
          </li>
        )
      : null;
  }
}

export const ReorderableItem = withDndReorder(BareReorderableItem, 'reorderableItem');
